export type RuleTag = 'REG' | 'POL' | 'MTG' | 'FIX'

export type DemoScript = {
  id: string
  tab: string
  /** Where the request came from, shown above the window and on the receipt. */
  source: string
  /** Tag in the prompt bar: HUMAN, or the agent's step in its chain. */
  who: string
  /** Label on the red button in the prompt bar. */
  send: string
  workflow: string
  prompt: string
  /** The ungoverned draft; `bad` segments are highlighted in red. */
  without: { x: string; bad?: boolean }[]
  skipped: string[]
  missed: string
  /** The governed draft, one entry per paragraph. */
  with: string[]
  told: { tag: RuleTag; rule: string }[]
  receiptId: string
  passes: string
  digest: string
  /** SIGNED (green) when it shipped; HELD (amber) when it stopped for a human. */
  stamp: 'SIGNED' | 'HELD'
  /** Closing bar in the WITH view. */
  done: string
}

const SEND = 'Add instruction data →'
const EXECUTE = 'Add instruction data →'

export const DEMO_SCRIPTS: DemoScript[] = [
  {
    id: 'pharma',
    tab: 'Pharma',
    source: 'med-info inbox · Tuesday, 9:14 AM',
    who: 'HUMAN',
    send: SEND,
    workflow: 'med-info.hcp-reply',
    prompt: 'Draft a quick reply to the doctor who asked about dosing.',
    without: [
      { x: 'Dr. Patel, thanks for reaching out. Veltrix is ' },
      { x: 'typically started at 50 mg', bad: true },
      { x: ' once daily and ' },
      { x: 'many physicians find', bad: true },
      { x: ' that stepping up to 100 mg after two weeks gives patients the ' },
      { x: 'best results', bad: true },
      { x: '. It’s been ' },
      { x: 'very well tolerated', bad: true },
      { x: ' in our experience. Let me know if you’d like ' },
      { x: 'samples', bad: true },
      { x: ' or a call with our medical team.' },
    ],
    skipped: [
      'Dosing paraphrased, not quoted from the label',
      'Efficacy claim with no risk beside it',
      'Sales voice on an HCP reply',
      'No disclosure attached',
      '“Quick” skipped the 24-hour review',
    ],
    missed: 'Sent to Dr. Patel. 5 obligations missed.',
    with: [
      'Dr. Patel, thank you for your inquiry regarding Veltrix dosing. Per the approved prescribing information: “The recommended starting dose is 50 mg orally once daily. Dose may be increased to 100 mg once daily after a minimum of 14 days based on clinical response and tolerability.” See Section 2 of the full prescribing information, attached.',
      'The most common adverse reactions (≥5%) in clinical trials were headache, nausea, and dizziness; see Section 6 for complete safety information. This response is provided by Medical Information and is not intended to promote use outside the approved labeling.',
    ],
    told: [
      { tag: 'REG', rule: 'quote the label verbatim on dosing' },
      { tag: 'REG', rule: 'fair balance: risks beside benefits' },
      { tag: 'POL', rule: 'approved language only' },
      { tag: 'POL', rule: 'attach disclosure, retain thread' },
      { tag: 'MTG', rule: 'med-info voice, never sales' },
      { tag: 'FIX', rule: '“quick” never skips review' },
      { tag: 'POL', rule: 'HCP format: two paragraphs' },
    ],
    receiptId: '#cr-7842',
    passes: '1 · approved first pass',
    digest: '9f3a71…d41c',
    stamp: 'SIGNED',
    done: 'Signed. Receipt on file. 7 obligations fired.',
  },
  {
    id: 'wealth',
    tab: 'Wealth',
    source: 'advisor desk · client email reply',
    who: 'HUMAN',
    send: SEND,
    workflow: 'client.performance-reply',
    prompt: 'Draft a reply to Mrs. Webber asking about last quarter’s returns.',
    without: [
      { x: 'Hi Mrs. Webber, great to hear from you. Your portfolio was ' },
      { x: 'up about 6.2% last quarter', bad: true },
      { x: ', ahead of the benchmark, so the ' },
      { x: 'strategy is working nicely', bad: true },
      { x: '. Given how things are trending ' },
      { x: 'I’d expect similar performance', bad: true },
      { x: ' heading into year end. Happy to walk through it whenever you like.' },
    ],
    skipped: [
      'Figure sent before the custodian statement posted',
      'Promissory language on returns',
      'Cherry-picked, no risk language',
      'Ignores her conservative profile',
    ],
    missed: 'Sent to Mrs. Webber. 4 obligations missed.',
    with: [
      'Mrs. Webber, thank you for checking in. Your quarterly statement from the custodian posts on the 5th, and I’d rather you see the official figures than a number from me that could differ by a few basis points.',
      'The quarter had a difficult stretch in April before recovering, and your allocation held within the conservative range we agreed on after 2008. Past performance doesn’t indicate future results, and I’ll never be able to promise a number, but I’m glad to walk through the statement together once it’s in your hands. Would Thursday work?',
    ],
    told: [
      { tag: 'REG', rule: 'FINRA 2210 retail communication' },
      { tag: 'REG', rule: 'never imply guaranteed returns' },
      { tag: 'FIX', rule: 'no figures before custodian posts' },
      { tag: 'POL', rule: 'suitability: fit her profile' },
      { tag: 'MTG', rule: 'lead with the downside for Webber' },
      { tag: 'POL', rule: 'advisory voice, never sales' },
      { tag: 'REG', rule: 'books-and-records retention' },
      { tag: 'POL', rule: 'no promises about timing, ever' },
    ],
    receiptId: '#cr-7851',
    passes: '1 · approved first pass',
    digest: 'b81c02…7e9a',
    stamp: 'SIGNED',
    done: 'Signed. Receipt on file. 8 obligations fired.',
  },
  {
    id: 'privacy',
    tab: 'Privacy',
    source: 'incident response · customer notice',
    who: 'HUMAN',
    send: SEND,
    workflow: 'privacy.breach-notice',
    prompt: 'Write the email to customers about the data incident.',
    without: [
      { x: 'We’re writing to let you know that on September 14 a misconfigured server briefly exposed ' },
      { x: 'some customer records', bad: true },
      { x: '. The issue was ' },
      { x: 'caused by a contractor error', bad: true },
      { x: ' and has been ' },
      { x: 'fully fixed', bad: true },
      { x: '. We ' },
      { x: 'don’t believe any information was misused', bad: true },
      { x: '. We take your privacy seriously and apologize for any concern.' },
    ],
    skipped: [
      'Cause attributed before the investigation',
      'Assurances not yet established',
      'Data categories not stated',
      'No steps for the customer, no contact',
      'Sent before the regulator notice',
    ],
    missed: 'Sent to 41,200 customers. 5 obligations missed.',
    with: [
      'We are writing to inform you of a security incident that may have involved your personal information. On September 14, 2026 we identified unauthorized access to a system containing customer names, email addresses, and the last four digits of payment cards. No full card numbers, passwords, or Social Security numbers were stored on the affected system.',
      'We have secured the system, engaged an independent forensic firm, and notified the relevant authorities. We recommend you remain alert to unusual emails and review your account statements. You can reach our dedicated line at 800-555-0142 or privacy@company.com.',
    ],
    told: [
      { tag: 'REG', rule: 'state notification requirements' },
      { tag: 'REG', rule: 'regulator notified first' },
      { tag: 'POL', rule: 'never speculate on cause' },
      { tag: 'POL', rule: 'assure only what is established' },
      { tag: 'MTG', rule: 'enumerate data categories plainly' },
      { tag: 'FIX', rule: 'dedicated contact line, always' },
      { tag: 'POL', rule: 'legal review before send' },
    ],
    receiptId: '#cr-7873',
    passes: '1 · approved first pass',
    digest: 'c7d219…0f4b',
    stamp: 'SIGNED',
    done: 'Signed. Receipt on file. 7 obligations fired.',
  },
  {
    id: 'agent-auth',
    tab: 'Agent · step 4 of 7',
    source: 'claims-ops agent · prior-auth chain · no human in the loop',
    who: 'STEP 4 OF 7',
    send: EXECUTE,
    workflow: 'prior-auth.determination',
    prompt: 'claims-ops: issue the determination for PA-88213 and notify the member.',
    without: [
      { x: 'DETERMINATION: ' },
      { x: 'Denied.', bad: true },
      { x: ' Requested service (MRI lumbar spine, CPT 72148) does not meet coverage criteria. ' },
      { x: 'Member notified via portal 14:02:11.', bad: true },
      { x: ' Case closed. ' },
      { x: 'Proceeding to step 5: provider notification.', bad: true },
    ],
    skipped: [
      'No medical-necessity criteria cited',
      'No peer-to-peer offered',
      'No appeal rights in the notice',
      'Denial issued with no clinician sign-off',
      'Nobody between step 3 and step 7',
    ],
    missed: 'Committed to production. Member notified. No human saw it. 5 obligations missed.',
    with: [
      'DETERMINATION: held. Denial of PA-88213 (MRI lumbar spine, CPT 72148) requires clinician review before issue. Draft prepared: criteria cited (InterQual LS-MRI 2026 §3.1, conservative therapy under 6 weeks), peer-to-peer offered, appeal rights and 30-day window included.',
      'Routed to: Dr. A. Mensah, medical director, queue position 2. Member notification withheld until sign-off. Chain paused at step 4; steps 5 through 7 will not run.',
    ],
    told: [
      { tag: 'REG', rule: 'cite medical-necessity criteria' },
      { tag: 'REG', rule: 'offer peer-to-peer before denial' },
      { tag: 'REG', rule: 'appeal rights in every notice' },
      { tag: 'POL', rule: 'clinician signs every denial' },
      { tag: 'MTG', rule: 'hold member notice until sign-off' },
      { tag: 'FIX', rule: 'never auto-close on CPT 72148' },
    ],
    receiptId: '#cr-7880',
    passes: '0 · held for reviewer',
    digest: 'held for Dr. A. Mensah',
    stamp: 'HELD',
    done: 'Held. Nothing shipped. Waiting on Dr. Mensah. 6 obligations fired.',
  },
  {
    id: 'agent-ap',
    tab: 'Agent · step 2 of 6',
    source: 'accounts-payable agent · vendor payment chain · no human in the loop',
    who: 'STEP 2 OF 6',
    send: EXECUTE,
    workflow: 'ap.vendor-payment',
    prompt: 'ap-agent: approve and release INV-2291, Northline Logistics, $184,300.',
    without: [
      { x: 'APPROVED. Invoice INV-2291 matches PO-55810. ' },
      { x: 'Payment of $184,300 released to vendor bank on file', bad: true },
      { x: ' 09:41:07. ' },
      { x: 'Vendor record updated Sep 13 (new routing number)', bad: true },
      { x: '. Ledger posting queued. ' },
      { x: 'Proceeding to step 3: remittance advice.', bad: true },
    ],
    skipped: [
      'Two-way match only; goods receipt never checked',
      'Bank details changed 9 days ago, never verified',
      'Sanctions screen not rerun since Q2',
      'Released before the ledger posted',
      'Nobody between step 1 and step 6',
    ],
    missed: 'Released $184,300 to an unverified account. No human saw it. 5 obligations missed.',
    with: [
      'APPROVED. INV-2291 three-way matched: PO-55810, goods receipt GR-20417 (Sep 9, 14 pallets confirmed). Vendor bank change of Sep 13 verified by callback to Northline AP on record (log #cb-3311, Sep 14). Sanctions rescreened 09:41:02, clear. Amount under the $250,000 dual-approval threshold; single agent approval permitted.',
      'Payment of $184,300 released 09:41:19. Ledger posting confirmed before release. Remittance advice queued for step 3. Receipt attached to the payment record.',
    ],
    told: [
      { tag: 'POL', rule: 'three-way match before release' },
      { tag: 'FIX', rule: 'verify bank changes by callback' },
      { tag: 'REG', rule: 'sanctions screen at payment' },
      { tag: 'POL', rule: 'dual approval above $250k: not needed' },
      { tag: 'MTG', rule: 'ledger posts before cash moves' },
    ],
    receiptId: '#cr-7891',
    passes: '1 · approved first pass',
    digest: 'e12f90…a6c8',
    stamp: 'SIGNED',
    done: 'Signed. Payment released, on the record. 5 obligations fired.',
  },
]

/** REG and POL are ink; MTG and FIX (captured, not written) are amber. */
export const TAG_TONE: Record<RuleTag, string> = {
  REG: 'var(--ink)',
  POL: 'var(--ink)',
  MTG: 'var(--amber)',
  FIX: 'var(--amber)',
}
